import { Grid } from '../core/Grid.js';
import { edgeAdjacentCells, parsePositionKey, positionKey, Position } from '../core/GridGeometry.js';
import { GridRenderer } from '../rendering/GridRenderer.js';
import { RenderConfig, COLORS, colorWithAlpha } from '../rendering/Renderer.js';
import { VortexHub } from './VortexHub.js';
import { PendingDelete } from './VortexHubInput.js';

export interface GateManagerRenderOptions {
    pendingDelete?: PendingDelete;
    pendingCreate?: Position | null;
    usedGateKeys?: Set<string>;
}

export class VortexHubRenderer extends GridRenderer {
    constructor(canvas: HTMLCanvasElement, grid: Grid, config: Partial<RenderConfig> = {}) {
        super(canvas, grid, config);
    }

    render(hub: VortexHub, gatesMode: boolean = false, options?: GateManagerRenderOptions): void {
        this.clear();
        this.drawGrid();
        if (gatesMode) {
            // Legacy Gate-Manager rendering, used only by the Vortex Hub tutorial.
            this.drawDigitSquares(hub);
            this.drawGatePairDashedLines(hub, true);
            this.drawGateManagerSquares(hub, options);
        } else if (hub.isBuilding()) {
            this.drawExistingGateLinks(hub);
            this.drawDigitSquares(hub);
            this.drawGatePairDashedLines(hub);
            this.drawBuildPath(hub);
            this.drawGateSquares(hub, options);
        } else {
            this.drawAutoDigitEdges(hub);
            this.drawDigitSquares(hub);
            this.drawGatePairDashedLines(hub);
            this.drawExistingGateLinks(hub);
            this.drawGateSquares(hub, options);
            this.drawPendingCreate(options?.pendingCreate);
        }

        this.drawKeyboardCursor();
    }

    /** Highlight the first gate of a pair being created (pending second click). */
    private drawPendingCreate(pos?: Position | null): void {
        if (!pos) return;
        const inset = this.config.cellSize * 0.16;
        this.highlightCell(pos, colorWithAlpha('purple', 0.25));
        this.drawRoundedSquare(pos, COLORS.purple, inset, false);
    }

    private drawAutoDigitEdges(hub: VortexHub): void {
        const autoDigitEdges = hub.getAutoDigitEdges();
        if (autoDigitEdges.size === 0) return;

        this.ctx.strokeStyle = COLORS.white;
        this.ctx.lineWidth = 4;
        this.ctx.lineCap = 'round';
        this.ctx.setLineDash([6, 6]);
        for (const ek of autoDigitEdges) {
            this.drawLinkSegment(ek);
        }
        this.ctx.setLineDash([]);
    }

    private drawExistingGateLinks(hub: VortexHub): void {
        const gateLinks = hub.getGateLinks();
        if (gateLinks.size === 0) return;

        this.ctx.strokeStyle = COLORS.purple;
        this.ctx.lineWidth = 8;
        this.ctx.lineCap = 'round';
        this.ctx.setLineDash([]);
        for (const linkKey of gateLinks) {
            this.drawLinkSegment(linkKey);
        }
    }

    private drawGatePairDashedLines(hub: VortexHub, includeLinked: boolean = false): void {
        const pairings = hub.getGatePairings();
        if (pairings.size === 0) return;

        const drawn = new Set<string>();
        this.ctx.strokeStyle = COLORS.purple;
        this.ctx.lineWidth = 4;
        this.ctx.lineCap = 'round';
        this.ctx.setLineDash([8, 6]);

        for (const [key, pairedKey] of pairings) {
            const posA = parsePositionKey(key);
            if (!includeLinked && hub.isGateLinked(posA)) continue;

            const pairId = key < pairedKey ? `${key}|${pairedKey}` : `${pairedKey}|${key}`;
            if (drawn.has(pairId)) continue;
            drawn.add(pairId);

            const posB = parsePositionKey(pairedKey);
            const centerA = this.getCellCenter(posA);
            const centerB = this.getCellCenter(posB);
            this.ctx.beginPath();
            this.ctx.moveTo(centerA.x, centerA.y);
            this.ctx.lineTo(centerB.x, centerB.y);
            this.ctx.stroke();
        }

        this.ctx.setLineDash([]);
    }

    private drawBuildPath(hub: VortexHub): void {
        const path = hub.getBuildPath();
        if (path.length === 0) return;

        this.ctx.strokeStyle = COLORS.purple;
        this.ctx.lineWidth = 8;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.setLineDash([]);

        this.ctx.beginPath();
        const start = this.getCellCenter(path[0]);
        this.ctx.moveTo(start.x, start.y);
        for (let i = 1; i < path.length; i++) {
            const point = this.getCellCenter(path[i]);
            this.ctx.lineTo(point.x, point.y);
        }
        this.ctx.stroke();

        // Large circle at start gate (cancel target)
        this.ctx.fillStyle = COLORS.purple;
        this.ctx.beginPath();
        this.ctx.arc(start.x, start.y, 14, 0, Math.PI * 2);
        this.ctx.fill();

        // Smaller circle at current end
        if (path.length > 1) {
            const end = this.getCellCenter(path[path.length - 1]);
            this.ctx.beginPath();
            this.ctx.arc(end.x, end.y, 8, 0, Math.PI * 2);
            this.ctx.fill();
        }
    }

    private drawLinkSegment(linkKey: string): void {
        const edge = this.grid.geometry.deserializeEdge(linkKey);
        if (!edge) return;
        const cells = edgeAdjacentCells(edge);
        if (cells.length !== 2) return;

        const centerA = this.getCellCenter(cells[0]);
        const centerB = this.getCellCenter(cells[1]);

        this.ctx.beginPath();
        this.ctx.moveTo(centerA.x, centerA.y);
        this.ctx.lineTo(centerB.x, centerB.y);
        this.ctx.stroke();
    }

    private drawDigitSquares(hub: VortexHub): void {
        const { cellSize } = this.config;
        const inset = cellSize * 0.25;
        const largestRegionId = hub.getLargestRegionId();
        const cellRegion = hub.getCellRegion();

        for (const [, pos] of this.grid.getAllDigitPositions()) {
            const key = positionKey(pos);
            const regionId = cellRegion.get(key);
            const inLargest = regionId !== undefined && regionId === largestRegionId;
            const color = inLargest ? COLORS.white : COLORS.red;
            this.drawRoundedSquare(pos, color, inset, true);
        }
    }

    private drawGateSquares(hub: VortexHub, options?: GateManagerRenderOptions): void {
        const { cellSize } = this.config;
        const inset = cellSize * 0.16;
        const pd = options?.pendingDelete;

        for (const gateKey of hub.getGates()) {
            const pos = parsePositionKey(gateKey);
            if (pd && (gateKey === pd.clickedGateKey || gateKey === pd.pairedGateKey)) {
                // Pending deletion (right-click confirm): red filled
                this.drawRoundedSquare(pos, COLORS.red, inset, true);
            } else {
                this.drawRoundedSquare(pos, COLORS.purple, inset, hub.isGateLinked(pos));
            }
        }
    }

    /**
     * Draw gate squares in Gate Manager mode:
     * - Unused linked pairs: hollow (can be instantly removed)
     * - Used linked pairs: filled (need confirmation)
     * - Pending-delete pair: red filled
     * - Unpaired/unlinked: hollow
     */
    private drawGateManagerSquares(hub: VortexHub, options?: GateManagerRenderOptions): void {
        const { cellSize } = this.config;
        const inset = cellSize * 0.16;
        const pendingDelete = options?.pendingDelete;
        const usedGateKeys = options?.usedGateKeys;

        for (const gateKey of hub.getGates()) {
            const pos = parsePositionKey(gateKey);
            const isPending = pendingDelete &&
                (gateKey === pendingDelete.clickedGateKey || gateKey === pendingDelete.pairedGateKey);

            if (isPending) {
                this.drawRoundedSquare(pos, COLORS.red, inset, true);
            } else {
                const isUsed = usedGateKeys?.has(gateKey) ?? false;
                this.drawRoundedSquare(pos, COLORS.purple, inset, isUsed);
            }
        }
    }
}
